import api, { unwrap } from './client';
import type { ApiResponse, PageResponse } from '@/types/api';
import type { EmployeeResponse } from '@/types/employee';
import type {
  ApprovalActionRecord,
  ApprovalDashboardSummary,
  ApprovalDelegation,
  ApprovalInstance,
  ApprovalMatrixRow,
  ApprovalMetadata,
  ApprovalWorkflow,
} from '@/types/approvals';

export const approvalsApi = {
  metadata: () =>
    unwrap(api.get<ApiResponse<ApprovalMetadata>>('/v1/approvals/metadata')),

  listWorkflows: (ownerModule?: string) =>
    unwrap(
      api.get<ApiResponse<ApprovalWorkflow[]>>('/v1/approvals/workflows', {
        params: ownerModule ? { ownerModule } : undefined,
      }),
    ),

  getWorkflow: (id: number) =>
    unwrap(api.get<ApiResponse<ApprovalWorkflow>>(`/v1/approvals/workflows/${id}`)),

  createWorkflow: (body: Partial<ApprovalWorkflow>) =>
    unwrap(api.post<ApiResponse<ApprovalWorkflow>>('/v1/approvals/workflows', body)),

  updateWorkflow: (id: number, body: Partial<ApprovalWorkflow>) =>
    unwrap(api.put<ApiResponse<ApprovalWorkflow>>(`/v1/approvals/workflows/${id}`, body)),

  cloneWorkflow: (id: number) =>
    unwrap(api.post<ApiResponse<ApprovalWorkflow>>(`/v1/approvals/workflows/${id}/clone`)),

  deactivateWorkflow: (id: number) =>
    unwrap(api.post<ApiResponse<{ status: string }>>(`/v1/approvals/workflows/${id}/deactivate`)),

  activateWorkflow: (id: number) =>
    unwrap(api.post<ApiResponse<{ status: string }>>(`/v1/approvals/workflows/${id}/activate`)),

  archiveWorkflow: (id: number) =>
    unwrap(api.post<ApiResponse<{ status: string }>>(`/v1/approvals/workflows/${id}/archive`)),

  matrix: () => unwrap(api.get<ApiResponse<ApprovalMatrixRow[]>>('/v1/approvals/matrix')),

  inbox: (
    status?: string,
    ownerModule?: string,
    mineOnly?: boolean,
    submittedByMe?: boolean,
    delegatedToMe?: boolean,
  ) =>
    unwrap(
      api.get<ApiResponse<ApprovalInstance[]>>('/v1/approvals/inbox', {
        params: {
          status,
          ownerModule,
          mineOnly: mineOnly ? true : undefined,
          submittedByMe: submittedByMe ? true : undefined,
          delegatedToMe: delegatedToMe ? true : undefined,
        },
      }),
    ),

  dashboard: (ownerModule?: string) =>
    unwrap(
      api.get<ApiResponse<ApprovalDashboardSummary>>('/v1/approvals/dashboard', {
        params: ownerModule ? { ownerModule } : undefined,
      }),
    ),

  getInstance: (id: number) =>
    unwrap(api.get<ApiResponse<ApprovalInstance>>(`/v1/approvals/instances/${id}`)),

  act: (id: number, action: string, comments?: string, delegateToUserId?: number) =>
    unwrap(
      api.post<ApiResponse<{ status: string }>>(`/v1/approvals/instances/${id}/act`, {
        action,
        comments,
        delegateToUserId,
      }),
    ),

  resubmit: (id: number) =>
    unwrap(api.post<ApiResponse<{ instanceId: number; status: string }>>(`/v1/approvals/instances/${id}/resubmit`)),

  timeline: (id: number) =>
    unwrap(api.get<ApiResponse<ApprovalActionRecord[]>>(`/v1/approvals/instances/${id}/timeline`)),

  lookupInstance: async (ownerModule: string, entityType: string, entityId: number) => {
    const instance = await unwrap(
      api.get<ApiResponse<ApprovalInstance | null>>('/v1/approvals/instances/lookup', {
        params: { ownerModule, entityType, entityId },
      }),
    );
    return instance ?? null;
  },

  batchLookupInstances: async (ownerModule: string, entityType: string, entityIds: number[]) => {
    if (entityIds.length === 0) {
      return {} as Record<number, ApprovalInstance | null>;
    }
    return unwrap(
      api.get<ApiResponse<Record<number, ApprovalInstance | null>>>('/v1/approvals/instances/lookup/batch', {
        params: { ownerModule, entityType, entityIds },
      }),
    );
  },

  listDelegations: () =>
    unwrap(api.get<ApiResponse<ApprovalDelegation[]>>('/v1/approvals/delegations')),

  createDelegation: (body: {
    delegateUserId: number;
    ownerModule?: string;
    reason?: string;
    effectiveFrom?: string;
    effectiveTo: string;
  }) => unwrap(api.post<ApiResponse<ApprovalDelegation>>('/v1/approvals/delegations', body)),

  deactivateDelegation: (id: number) =>
    unwrap(api.post<ApiResponse<{ status: string }>>(`/v1/approvals/delegations/${id}/deactivate`)),

  delegateCandidates: (params?: {
    search?: string;
    page?: number;
    size?: number;
    activeOnly?: boolean;
    sortBy?: string;
    sortDirection?: string;
  }) =>
    unwrap(
      api.get<ApiResponse<PageResponse<EmployeeResponse>>>('/v1/approvals/delegate-candidates', {
        params,
      }),
    ),
};
